import React from "react";
import QuillEditor from "../util/QuillEditor";
import styles from "./PageEdit.module.css";
import TextInput from "./TextInput";
import ImageSelector from "./ImageSelector";
import PageInfoProfileEdit from "./PageInfoProfileEdit";
import PageInfoListItemEdit from "./PageInfoListItemEdit";
import { FaPlus } from "react-icons/fa";
import { TiDelete } from "react-icons/ti";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const COMPONENT_MAP = {
    profile: PageInfoProfileEdit,
    listItem: PageInfoListItemEdit,
};

let idCounter = 0;
const genSectionKey = (type) => `${type}_${Date.now()}_${idCounter++}`;

function SortableItem({ id, onRemove, children }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        position: 'relative',
        width: '80%',
    };

    return (
        <div ref={setNodeRef} style={style}>
            <div {...attributes} {...listeners} style={{ cursor: 'grab', textAlign: 'center', padding: '4px', userSelect: 'none' }}>
                ⠿
            </div>
            {children}
            <TiDelete
                onClick={onRemove}
                style={{ position: 'absolute', top: 0, right: 0, cursor: 'pointer' }}
                className={`${styles.iconBnt} ${styles.deleteIcon}`}
            />
        </div>
    );
}

export default function PageInfoListEdit({
    sectionKey, header, text, imageUrl, id, secName, items, updateField, ...itemContent
}) {
    const safeItems = Array.isArray(items) ? items : [];
    const persistItems = (newItems) => updateField(sectionKey, "items", newItems);

    const handleAdd = (type) => {
        persistItems([...safeItems, { sectionKey: genSectionKey(type), type }]);
    };

    const handleRemove = (childSectionKey) => {
        persistItems(safeItems.filter(i => i.sectionKey !== childSectionKey));
        updateField(sectionKey, childSectionKey, undefined); // drop the now-orphaned content object
    };

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

    const handleDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) return;
        const oldIndex = safeItems.findIndex(i => i.sectionKey === active.id);
        const newIndex = safeItems.findIndex(i => i.sectionKey === over.id);
        persistItems(arrayMove(safeItems, oldIndex, newIndex));
    };

    return (
        <div className={styles.pageInfoEdit}>
            <h2>{header}</h2>

            <TextInput label={"Header:"} value={header} onChange={(e) => updateField(sectionKey, "header", e.target.value)} />
            <TextInput label={"Id:"} value={id} onChange={(e) => updateField(sectionKey, "secId", e.target.value)} />
            <TextInput label={"Sektion navn:"} value={secName} onChange={(e) => updateField(sectionKey, "secName", e.target.value)} />

            <QuillEditor value={text || ""} onChange={(value) => updateField(sectionKey, "text", value)} placeholder="Write your content here..." />

            <ImageSelector value={imageUrl || ""} updateField={(val) => updateField(sectionKey, "imageUrl", val)} />

            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={safeItems.map(i => i.sectionKey)} strategy={verticalListSortingStrategy}>
                    {safeItems.map((item) => {
                        const ItemComponent = COMPONENT_MAP[item.type];
                        if (!ItemComponent) return null;

                        return (
                            <SortableItem key={item.sectionKey} id={item.sectionKey} onRemove={() => handleRemove(item.sectionKey)}>
                                <ItemComponent
                                    sectionKey={item.sectionKey}
                                    parentSectionKey={sectionKey}
                                    updateField={updateField}
                                    {...(itemContent[item.sectionKey] || {})}
                                />
                            </SortableItem>
                        );
                    })}
                </SortableContext>
            </DndContext>

            <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                <div className={styles.addImageItem} onClick={() => handleAdd('profile')} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '.5rem' }}>
                    <FaPlus size={18} /> Add Profile
                </div>
                {/* <div className={styles.addImageItem} onClick={() => handleAdd('listItem')} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '.5rem' }}>
                    <FaPlus size={18} /> Add List Item
                </div> */}
            </div>
        </div>
    );
}